(function () {
  'use strict';

  const { layout } = window.Universe;

  class Marquee {
    constructor(line) {
      this._timer = null;
      this.init(line);
    }

    isEagerStart() {
      const index = this.DOM.line.dataset.sectionIndex;
      return this.DOM.line.closest('.js-announcement-bar') || index === '1' || window.Shopify?.designMode;
    }

    setup() {
      const line = this.DOM.line;
      if (!line) return;

      // Collect all original items that are direct children of the line.
      // These form one complete "cycle" — the repeating unit.
      const items = Array.from(line.querySelectorAll(':scope > .js-marquee-item'));

      if (!items.length) {
        if (this.isEagerStart()) {
          line.classList.add('marquee-animated');
        } else {
          this.startOnInteraction();
        }
        return;
      }

      // Wrap all items in a single cycle container so we can clone the
      // whole group at once, regardless of how many blocks the user added.
      const cycle = document.createElement('div');
      cycle.className = 'marquee__cycle';
      items.forEach((item) => cycle.appendChild(item));
      line.appendChild(cycle);
      this.DOM.cycle = cycle;

      // ResizeObserver drives all recalculation — initial paint and every
      // resize. Debounced to avoid thrashing during continuous resize events.
      this.observer = new ResizeObserver(() => {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this._recalculate(), 60);
      });
      this.observer.observe(line);
    }

    _recalculate() {
      const { line, cycle } = this.DOM;
      if (!line || !cycle) return;

      // Remove clones from any previous calculation.
      line.querySelectorAll('.marquee__cycle--clone').forEach((el) => el.remove());

      // getBoundingClientRect gives the rendered width of one full cycle,
      // including item margins — the exact distance to animate by.
      const cycleW = cycle.getBoundingClientRect().width;
      const containerW = (layout.window && layout.window.width) || window.innerWidth;

      if (!cycleW || !containerW) return;

      // We need: totalTrackWidth > containerW + cycleW
      // so that at every frame of the animation there is always content
      // filling the viewport on both sides of the seam.
      // Cap at 30 clones to protect against very small single-string content.
      const clones = Math.min(30, Math.max(1, Math.ceil(containerW / cycleW) + 1));
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < clones; i++) {
        const clone = cycle.cloneNode(true);
        clone.classList.add('marquee__cycle--clone');
        clone.setAttribute('aria-hidden', 'true');
        fragment.appendChild(clone);
      }
      line.appendChild(fragment);

      // Duration: cycleW / (speed × 60px/s) gives consistent visual speed
      // regardless of how much content the user puts in.
      // data-speed is a multiplier (schema range 0.1–5, default 1.2).
      const speed = Math.max(0.01, parseFloat(line.dataset.speed) || 1);
      line.style.setProperty('--_marquee-w', `${cycleW}px`);
      line.style.setProperty('--_marquee-dur', `${(cycleW / (speed * 60)).toFixed(2)}s`);

      if (!line.classList.contains('marquee-animated')) {
        if (this.isEagerStart()) {
          line.classList.add('marquee-animated');
        } else {
          this.startOnInteraction();
        }
      }
    }

    startOnInteraction() {
      const start = () => {
        if (this.DOM && this.DOM.line) {
          this.DOM.line.classList.add('marquee-animated');
        }
        this.removeInteractionListeners();
      };

      this.interactionEvents = ['pointerdown', 'scroll', 'keydown'];
      this.interactionHandler = start;

      for (let i = 0; i < this.interactionEvents.length; i++) {
        window.addEventListener(this.interactionEvents[i], this.interactionHandler, {
          once: true,
          passive: true,
        });
      }
    }

    removeInteractionListeners() {
      if (this.interactionEvents && this.interactionHandler) {
        for (let i = 0; i < this.interactionEvents.length; i++) {
          window.removeEventListener(this.interactionEvents[i], this.interactionHandler);
        }
        this.interactionEvents = null;
        this.interactionHandler = null;
      }
    }

    destroy() {
      clearTimeout(this._timer);

      if (this.DOM) {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.removeInteractionListeners();

        const { line, cycle } = this.DOM;
        if (cycle) {
          // Remove clones, then unwrap original items back into the line
          // so the DOM is clean if this section is re-initialised.
          line.querySelectorAll('.marquee__cycle--clone').forEach((el) => el.remove());
          Array.from(cycle.children).forEach((child) => line.insertBefore(child, cycle));
          cycle.remove();
        }

        line.classList.remove('marquee-animated');
        line.style.removeProperty('--_marquee-w');
        line.style.removeProperty('--_marquee-dur');
        this.DOM = undefined;
      }
    }

    init(line) {
      if (line) {
        this.DOM = { line };
        this.sectionId = line.closest('.shopify-section')?.id || null;
        this.setup();
      }
    }
  }

  class Marquees {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.lines.length; i++) {
        this.instances.push(new Marquee(this.DOM.lines[i]));
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.instances.length; i++) {
          this.instances[i].destroy();
        }
        this.instances = [];
        this.DOM = undefined;
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      this.destroySection(sectionEl);

      if (!this.DOM) {
        this.DOM = {};
        this.instances = [];
      }

      const newLines = sectionEl.querySelectorAll('.js-marquee-line');
      for (let i = 0; i < newLines.length; i++) {
        this.instances.push(new Marquee(newLines[i]));
      }

      this.DOM.lines = document.querySelectorAll('.js-marquee-line');
    }

    destroySection(sectionEl) {
      if (!this.instances) return;

      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;

      for (let i = this.instances.length - 1; i >= 0; i--) {
        const instance = this.instances[i];
        if (instance.sectionId === targetId) {
          instance.destroy();
          this.instances.splice(i, 1);
        }
      }
    }

    init(container = document) {
      const lines = container.querySelectorAll('.js-marquee-line');

      if (lines.length) {
        this.DOM = {};
        this.DOM.lines = lines;
        this.instances = [];

        this.setup();
      }
    }
  }

  window.Universe.Marquees = Marquees;
  window.Universe.Marquee = Marquee;
})();
